import { NgClass, JsonPipe } from '@angular/common';
import { Component, Inject, inject, NgModule, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  NgModel,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatError, MatFormField, MatInput } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatOption, MatSelect, MatSelectTrigger } from '@angular/material/select';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { API_PARAMS } from '../../../constants/api.constants';
import { CITIES } from '../../../constants/location.constants';
import {
  AppointmentInfoField,
  BusinessCheckupSettingsRequest,
  PersonalCheckupSettingsRequest,
  District,
} from '../../../interfaces/appointment-info.model';
import { ModifyExamReportDialogModel } from '../../../interfaces/dialogs/modify-exam-report-dialog.model';
import { AppointmentInfoService } from '../../../services/appointment-info.service';
import { SharedStorageService } from '../../../services/shared-storage.service';
import { emailValidator } from '../../../utils/validator.util';
import { ID_LOGIN_TYPE } from '../../../constants/common.constants';
import { ExamItemName } from '../../../interfaces/reserve-confirm-survey.model';
import { AddExamInfo } from '../../../interfaces/reserve-add.model';

/**
 * 修改檢查資料與報告dialog組件
 */
@Component({
  selector: 'app-modify-exam-report-dialog',
  standalone: true,
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    MatError,
    MatInput,
    MatOption,
    MatSelect,
    MatSelectTrigger,
    MatCheckbox,
    MatRadioButton,
    MatRadioGroup,
    MatButton,
    MatFormField,
    NgClass,
    JsonPipe,
    FormsModule,
  ],
  templateUrl: './modify-exam-report-dialog.component.html',
  styleUrl: './modify-exam-report-dialog.component.scss',
})
export class ModifyExamReportDialogComponent implements OnInit {
  translateService: TranslateService = inject(TranslateService);
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  appointmentInfoService: AppointmentInfoService = inject(
    AppointmentInfoService
  );

  showFormError: boolean = false; // 是否顯示錯誤訊息
  languages: Array<AppointmentInfoField> = []; // 檢查報告語言
  isShowModifyContactAddress: boolean = true;// 是否顯示修改檢前資料地址
  isChecked:boolean; // 是否勾選同檢前資料
  noticeSendType: string = ''; // 檢前資料寄送方式（代碼）1：個別寄送 2：承辦領取 3：不須寄送
  deliveryType: string = ''; // 報告領取方式（代碼）1：個別寄送 2：承辦領取 3：不須寄送
  private isInitializing: boolean = true; // 是否正在初始化（避免初始化時觸發 valueChanges）


  form = new FormGroup({
    city: new FormControl('', [Validators.required]), // 檢前資料縣市
    district: new FormControl<{
      areaEngName: string;
      areaName: string;
      zipCode: string;
    } | null>(null, [Validators.required]), // 檢前資料區域
    address: new FormControl('', [Validators.required]), // 檢前資料地址
    deliveryMethod: new FormControl(API_PARAMS.DELIVERY_METHOD.DOMESTIC.code, [
      Validators.required,
    ]), // 領取方式
    reportCity: new FormControl('', [Validators.required]), // (郵寄國內)檢查報告寄送縣市
    reportDistrict: new FormControl<{
      areaEngName: string;
      areaName: string;
      zipCode: string;
    } | null>(null, [Validators.required]), // (郵寄國內)檢查報告寄送區域
    reportAddress: new FormControl('', [Validators.required]), // (郵寄國內)檢查報告寄送地址
    deliveryRecipient: new FormControl('', [Validators.required]), // 收件人資料
    recipient: new FormControl(''), // (非本人)報告收件人姓名
    recipientCell: new FormControl(''), // (非本人)報告收件人手機號碼
    foreignAddress: new FormControl(''), // (郵寄國外)寄送地址
    email: new FormControl(''), // (電子檔)信箱
    language: new FormControl('', [Validators.required]), // 檢查報告語言
  });

  // 是否為個人預約
    get isPersonalReserve(): boolean {
      if(this.sharedStorageService.get('reserveType')){
        //預約流程
        return (
                this.sharedStorageService.get('reserveType') === ID_LOGIN_TYPE.PERSONAL
              );
      }else{
        //會員中心-我的預約
        if(this.sharedStorageService.get('myReservation')?.companyNo){
          //企業
          return false;
        }else{
          //個人
          return true;
        }
      }
    }
    
  // (非本人)報告收件人手機號碼-格式驗證器
  phoneValidator(control: FormControl): ValidationErrors | null {
    const value = control.value;
    if (value) {
      const phoneRegex = /^\d{10}$/; // 手機號碼為10位數字
      if (!phoneRegex.test(value)) {
        return {
          invalidFormat: this.translateService.instant(
            'error.phone-error.format'
          ),
        };
      }
    }
    return null;
  }
  constructor(
    public dialogRef: MatDialogRef<ModifyExamReportDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ModifyExamReportDialogModel
  ) {
    // 檢前資料寄送方式
    //若有企業
    if(!this.isPersonalReserve){
      this.noticeSendType = data.noticeSendType;
    }
    // 初始化檢後資料表單值
    this.deliveryType = data.deliveryMethod;
    
    // 收件人資料
    this.form.get('deliveryRecipient')?.setValue(data.deliveryRecipient);
    console.log('dialog-data.deliveryMethod',data.deliveryMethod);
    this.form.get('deliveryMethod')?.setValue(data.deliveryMethod);
    //初始化表格必填規則
    this.formSetting();
    
    // 檢前資料-取得該 city 對應的 district 陣列(初始化下拉選單)
    this.districts = CITIES.find(c => c.cityName === data.city)?.areaList || [];
    // 檢前資料-用 areaName找到對應的 District 物件(因zipCode非唯一值，例:新竹市行政區都是300)
    const initDistrict =
      this.districts.find(d => d.areaName === data.district) ?? null;

    // 檢查報告-取得該 city 對應的 district 陣列(初始化下拉選單)
    this.reportDistricts =
      CITIES.find(c => c.cityName === data.reportCity)?.areaList || [];
    console.log('data.reportCity:', data.reportCity);
    console.log('data.reportDistrict:', data.reportDistrict);
    console.log('this.reportDistricts:', this.reportDistricts);

    // 檢查報告-用 areaName找到對應的 District 物件(因zipCode非唯一值，例:新竹市行政區都是300)
    const initReportDistrict =
      this.reportDistricts.find(d => d.areaName === data.reportDistrict) ??
      null;
    console.log('initReportDistrict:', initReportDistrict);

    //是否禁用檢前地址編輯，自會員中心-我的預約
    this.isShowModifyContactAddress = data.isShowModifyContactAddress ?? true;

    console.log('isShowModifyContactAddress:', this.isShowModifyContactAddress);
    this.form.patchValue({
      city: data.city || '',
      district: initDistrict || null,
      address: data.address || '',
      deliveryMethod:
        data.deliveryMethod || API_PARAMS.DELIVERY_METHOD.DOMESTIC.code,
      reportCity: data.reportCity || '',
      reportDistrict: initReportDistrict || null,
      reportAddress: data.reportAddress || '',
      deliveryRecipient: data.deliveryRecipient || '',
      recipient: data.recipient || '',
      recipientCell: data.recipientCell || '',
      foreignAddress: data.foreignAddress || '',
      email: data.email || '',
      language: data.language || '',
    });
    console.log('data:', data);

    console.log('patchValue 後 this.form.reportCity', this.form.get('reportCity')?.value);
    console.log('patchValue 後 this.form.reportDistrict (完整物件)', this.form.get('reportDistrict')?.value);
    console.log('patchValue 後 this.form.reportDistrict.areaName', this.form.get('reportDistrict')?.value?.areaName);
    //初始化判斷是否同檢前資
    if(this.sharedStorageService.get('bookingInfo')?.isChecked){
      //預約流程
      this.isChecked= this.sharedStorageService.get('bookingInfo')?.isChecked ?? false;
    }else{
      //會員中心-我的預約
    const preExamAddress =''+ data.city + data.district + data.address;
    const postExamAddress=''+ data.reportCity + data.reportDistrict + data.reportAddress;
    console.log('preExamAddress:',preExamAddress);
    console.log('postExamAddress:',postExamAddress);
    if(preExamAddress === postExamAddress){
      this.isChecked = true;
    }else{
      this.isChecked = false;
    }
    const bookingInfo = this.sharedStorageService.get('bookingInfo');
    if(bookingInfo){
      bookingInfo.isChecked = this.isChecked;
      this.sharedStorageService.set('bookingInfo', bookingInfo);
    }

    }

    console.log('this.isChecked:', this.isChecked);
    // 只有當 isChecked 為 true 時才調用 toggleSameAsPreExamInfo
    // 避免清空已初始化的報告地址
    if (this.isChecked) {
      this.toggleSameAsPreExamInfo(true);
    }
    

    // 監聽deliveryMethod變化時，添加/刪除相關欄位校驗
    this.form.get('deliveryMethod')?.valueChanges.subscribe(value => {
      if (value === API_PARAMS.DELIVERY_METHOD.DOMESTIC.code) {
        //寄國內
        console.log("寄國內");
        this.form.get('reportCity')?.setValidators([Validators.required]);
        this.form.get('reportDistrict')?.setValidators([Validators.required]);
        this.form.get('reportAddress')?.setValidators([Validators.required]);
        this.form.get('foreignAddress')?.clearValidators();
        this.form.get('foreignAddress')?.setValue(null); 
        this.form.get('email')?.clearValidators();
      } else if (value === API_PARAMS.DELIVERY_METHOD.FOREIGN.code) {
        console.log("寄國外");
        this.form.get('foreignAddress')?.setValidators([Validators.required]);
        this.form.get('reportCity')?.clearValidators();
        this.form.get('reportDistrict')?.clearValidators();
        this.form.get('reportAddress')?.clearValidators();
        this.form.get('email')?.clearValidators();
        this.form.get('reportCity')?.setValue(null); 
        this.form.get('reportDistrict')?.setValue(null); 
        this.form.get('reportAddress')?.setValue(null); 
        this.toggleSameAsPreExamInfo(false);
        // (this.form.get('sameAsPreExamInfoChecked') as unknown as FormControl<boolean>)?.setValue(false);
        this.form.get('email')?.setValue(null); 
      } 
      this.form.get('reportCity')?.updateValueAndValidity();
      this.form.get('reportDistrict')?.updateValueAndValidity();
      this.form.get('reportAddress')?.updateValueAndValidity();
      this.form.get('foreignAddress')?.updateValueAndValidity();
      this.form.get('email')?.updateValueAndValidity();
    });
    //是否禁用檢前地址編輯條件
    if (data.isShowModifyContactAddress === false) {  
      this.form.get('city')?.disable();
      this.form.get('district')?.disable();
      this.form.get('address')?.disable();
    }

  }

  ngOnInit(): void {
    
    // 檢前資料寄送地址
    // city 選擇變更時
    console.log('cities:', this.cities);
    let previousCity = this.form.get('city')?.value;
    this.form
      .get('city')
      ?.valueChanges.subscribe((selectedCityName: string | null) => {
        console.log('city valueChanges 觸發, 新值:', selectedCityName, '舊值:', previousCity);

        // 如果城市沒有真正改變，不要清空 district
        if (selectedCityName === previousCity) {
          console.log('城市沒有改變，跳過清空 district');
          return;
        }

        previousCity = selectedCityName;

        if (!selectedCityName) {
          this.districts = [];
          this.form.get('district')?.setValue(null);
          this.selectedDistrict = null;
          return;
        }

        const city = CITIES.find(c => c.cityName === selectedCityName);
        this.districts = city ? city.areaList : [];
        console.log('城市改變，清空 district');
        this.form.get('district')?.setValue(null);
        this.selectedDistrict = null;
      });
    // district 選擇變更時
    this.form.get('district')?.valueChanges.subscribe(
      (
        selectedDistrictName: {
          areaEngName: string;
          areaName: string;
          zipCode: string;
        } | null
      ) => {
        if (!selectedDistrictName) {
          this.selectedDistrict = null;
          return;
        }

        const district = this.districts.find(
          a => a.areaName === selectedDistrictName.areaName
        );
        this.selectedDistrict = district || null;
        console.log('檢查報告form-district:', this.form.get('district')?.value);
      }
    );

    // 檢查報告寄送地址
    // city 選擇變更時
    let previousReportCity = this.form.get('reportCity')?.value;
    this.form
      .get('reportCity')
      ?.valueChanges.subscribe((selectedCityName: string | null) => {
        console.log('reportCity valueChanges 觸發, 新值:', selectedCityName, '舊值:', previousReportCity);

        // 如果城市沒有真正改變，不要清空 district
        if (selectedCityName === previousReportCity) {
          console.log('城市沒有改變，跳過清空 reportDistrict');
          return;
        }

        previousReportCity = selectedCityName;

        if (!selectedCityName) {
          this.reportDistricts = [];
          this.form.get('reportDistrict')?.setValue(null);
          this.selectedReportDistrict = null;
          return;
        }

        const city = CITIES.find(c => c.cityName === selectedCityName);
        this.reportDistricts = city ? city.areaList : [];
        console.log('城市改變，清空 reportDistrict');
        this.form.get('reportDistrict')?.setValue(null);
        this.selectedReportDistrict = null;

        // 檢查是否與檢前資料相同
        if (!this.isInitializing) {
          this.checkAndUpdateIsChecked();
        }
      });
    // district 選擇變更時
    this.form.get('reportDistrict')?.valueChanges.subscribe((selectedDistrictName) => {
      if (!selectedDistrictName) {
        this.selectedReportDistrict = null;
        return;
      }
      
      const district = this.reportDistricts.find(
        a => a.areaName === selectedDistrictName.areaName
      );
      this.selectedReportDistrict = district || null;

      // 檢查是否與檢前資料相同
      if (!this.isInitializing) {
        this.checkAndUpdateIsChecked();
      }
    });

    // reportAddress 選擇變更時
    this.form.get('reportAddress')?.valueChanges.subscribe((reportAddress) => {
      // 檢查是否與檢前資料相同
      if (!this.isInitializing) {
        this.checkAndUpdateIsChecked();
      }
    });

    //監聽收件人資料變化時，添加/刪除相關欄位校驗
    this.form.get('deliveryRecipient')?.valueChanges.subscribe(value => {
      if (value === API_PARAMS.DELIVERY_METHOD.NOT_SELF.code) {
        this.form.get('recipient')?.setValidators([Validators.required]);
        this.form
          .get('recipientCell')
          ?.setValidators([
            Validators.required,
            (control: FormControl) => this.phoneValidator(control),
          ]);
      } else {
        this.form.get('recipient')?.clearValidators();
        this.form.get('recipientCell')?.clearValidators();
      }
      this.form.get('recipient')?.updateValueAndValidity();
      this.form.get('recipientCell')?.updateValueAndValidity();
    });

    

    if (this.isPersonalReserve) {
      console.log("個人預約");
      const sharedStorageExamItemList =
        this.sharedStorageService.get('examItemList') ?? [];
      const newExamItemList = sharedStorageExamItemList.map(item => ({
            examItem: item.examItem,
            examName: item.examName,
            selectFlag: item.selectFlag ?? 1,
          }))?? [];; //轉換成API接受的request格式
    
    // 取得加選檢項
          const addList: Array<ExamItemName> =
            this.sharedStorageService
              .get('addExamItemList')
              ?.map((addExamInfo: AddExamInfo) => {
                return {
                  examItem: addExamInfo.examItem,
                  examName: addExamInfo.examName,
                };
              }) ?? [];

      // 查詢報告語言打API CB-069
    const userPlanRequest = this.sharedStorageService.get('userPlanRequest');
    const myReservation = this.sharedStorageService.get('myReservation');
    const request: PersonalCheckupSettingsRequest = {
      dataType: API_PARAMS.CHECKUP_SETTINGS_DATATYPE.LANGUAGE,
      caseNo: myReservation?.caseNo ?? this.sharedStorageService.get('caseNo') ??'',
      companyNo: '1150',
      orgNo: userPlanRequest?.orgNo ?? '',
      gender: userPlanRequest?.gender ?? '',
      idNo: userPlanRequest?.idNo ?? '',
      UUID: userPlanRequest?.uuid ?? '',
      bookDate: myReservation?.bookDate ?? '',
      examItemList: newExamItemList,
      addList: addList,
    };
    this.appointmentInfoService.checkupSettings(request).subscribe(res => {
      this.languages = (res.data?.languInfos ?? []).sort(
        (a, b) => Number(a.uno) - Number(b.uno)
      );
    });

    }else{
      console.log("企業預約");
      // 取得預約檢項    
    const sharedStorageExamItemList =
      this.sharedStorageService.get('examItemList') ?? [];
    const newExamItemList = sharedStorageExamItemList.map(item => ({
      examItem: item.examItem,
      examName: item.examName
    }));
    // 取得加選檢項
    const sharedStorageAddList =
      this.sharedStorageService.get('addExamItemList')?? [];
    const addList = sharedStorageAddList.map(item => ({
      examItem: item.examItem,
      examName: item.examName
    })); 
    const businessPlanRequest = this.sharedStorageService.get('businessPlanRequest');
    const idLoginRequest = this.sharedStorageService.get('idLoginRequest');//走漸進式登入
    const businessLoginInfo = this.sharedStorageService.get('businessLoginInfo');//走會員登入
    const createMemberRequest = this.sharedStorageService.get('createMemberRequest');//走會員登入
    const bookingInfo = this.sharedStorageService.get('bookingInfo');
    const myReservation = this.sharedStorageService.get('myReservation');
    const request: BusinessCheckupSettingsRequest = {
            dataType: API_PARAMS.CHECKUP_SETTINGS_DATATYPE.LANGUAGE,
            caseNo: myReservation?.caseNo ?? this.sharedStorageService.get('caseNo') ??'',
            companyNo: businessPlanRequest?.companyNo ?? myReservation?.companyNo ??'',
            familyFlag: idLoginRequest?.identity ?? businessLoginInfo?.identity ?? myReservation?.familyFlag ?? API_PARAMS.IDENTITY.EMPLOYEE,
            orgNo: businessPlanRequest?.orgNo ?? bookingInfo?.orgNo ?? '',
            gender: businessPlanRequest?.gender ?? createMemberRequest?.gender ?? '',
            idNo: idLoginRequest?.idNo ?? createMemberRequest?.idNo ?? '',
            bookDate: myReservation?.bookDate ?? '',
            examItemList: newExamItemList,
            addList: addList,
          };
          console.log('企業檢查request:', request);
          // CB-074
          this.appointmentInfoService
            .businessCheckupSettings(request)
            .subscribe(res => {
              this.languages = (res.data?.languInfos ?? []).sort(
                (a, b) => Number(a.uno) - Number(b.uno)
              );
            });
    }

    if(this.deliveryType ==='1'||this.deliveryType ==='0'){
      //如果是個別寄送，則預設為郵寄國內
      this.form.get('deliveryMethod')?.setValue(API_PARAMS.DELIVERY_METHOD.DOMESTIC.code);
      }else{
        //清除檢核
        console.log('清除檢核');
        this.form.get('deliveryMethod')?.clearValidators();
        this.form.get('reportCity')?.clearValidators();
        this.form.get('reportDistrict')?.clearValidators();
        this.form.get('reportAddress')?.clearValidators();
        this.form.get('foreignAddress')?.clearValidators();
        this.form.get('email')?.clearValidators();
        this.form.get('deliveryRecipient')?.clearValidators();
        this.form.get('recipient')?.clearValidators();
        this.form.get('recipientCell')?.clearValidators();
        
        this.form.get('deliveryMethod')?.updateValueAndValidity({ emitEvent: false });
        this.form.get('reportCity')?.updateValueAndValidity({ emitEvent: false });
        this.form.get('reportDistrict')?.updateValueAndValidity({ emitEvent: false });
        this.form.get('reportAddress')?.updateValueAndValidity({ emitEvent: false });
        this.form.get('foreignAddress')?.updateValueAndValidity({ emitEvent: false });
        this.form.get('email')?.updateValueAndValidity({ emitEvent: false });
        this.form.get('deliveryRecipient')?.updateValueAndValidity({ emitEvent: false });
        this.form.get('recipient')?.updateValueAndValidity({ emitEvent: false });
        this.form.get('recipientCell')?.updateValueAndValidity({ emitEvent: false });
      }

      // 初始化完成，允許 valueChanges 觸發 isChecked 更新
      this.isInitializing = false;
  }

  // 更新 bookingInfo 中的 isChecked 狀態
  updateBookingInfoIsChecked() {
    const bookingInfo = this.sharedStorageService.get('bookingInfo');
    if (bookingInfo) {
      bookingInfo.isChecked = this.isChecked;
      this.sharedStorageService.set('bookingInfo', bookingInfo);
    }
  }

  formSetting(){
      const value = this.form.get('deliveryMethod')?.value ?? '';
      if (value === API_PARAMS.DELIVERY_METHOD.DOMESTIC.code) {
        //寄國內
        console.log("寄國內");
        this.form.get('reportCity')?.setValidators([Validators.required]);
        this.form.get('reportDistrict')?.setValidators([Validators.required]);
        this.form.get('reportAddress')?.setValidators([Validators.required]);
        this.form.get('foreignAddress')?.clearValidators();
        this.form.get('email')?.clearValidators();
      } else if (value === API_PARAMS.DELIVERY_METHOD.FOREIGN.code) {
        console.log("寄國外");
        this.form.get('foreignAddress')?.setValidators([Validators.required]);
        this.form.get('reportCity')?.clearValidators();
        this.form.get('reportDistrict')?.clearValidators();
        this.form.get('reportAddress')?.clearValidators();
        this.form.get('email')?.clearValidators();
      } 
      this.form.get('reportCity')?.updateValueAndValidity();
      this.form.get('reportDistrict')?.updateValueAndValidity();
      this.form.get('reportAddress')?.updateValueAndValidity();
      this.form.get('foreignAddress')?.updateValueAndValidity();
      this.form.get('email')?.updateValueAndValidity();
    
  }
  // 取得檢前資料地址錯誤訊息
  getPreExamAddressErrorMessage(): string {
    if (!this.showFormError) return '';
    const errors: string[] = [];
    if (this.form.get('city')?.errors)
      errors.push(this.translateService.instant('appointment-info.city'));
    if (this.form.get('district')?.errors)
      errors.push(this.translateService.instant('appointment-info.district'));
    if (this.form.get('address')?.errors)
      errors.push(
        this.translateService.instant('appointment-info.detailed-address')
      );
    return errors.length
      ? this.translateService.instant('error.please-fill-in', {
          fieldName: errors.join('、'),
        })
      : '';
  }

  

  // 勾選/取消勾選"同檢前資料"
  private originalRecipient: string | null = null;
  private originalRecipientCell: string | null = null;

  toggleSameAsPreExamInfo(checked: boolean) {
    console.log('toggleSameAsPreExamInfo called, checked:', checked);
    if (checked) {
      // 勾選前先存收件人原始值
      this.originalRecipient = this.form.get('recipient')?.value ?? '';
      this.originalRecipientCell = this.form.get('recipientCell')?.value ?? '';

      // 用檢前資料覆蓋報告地址
      const cityValue = this.form.get('city')?.value ?? '';

      // 更新 reportDistricts 陣列
      this.reportDistricts = CITIES.find(c => c.cityName === cityValue)?.areaList || [];
      console.log('更新後的 reportDistricts:', this.reportDistricts);

      const districtValue = this.form.get('district')?.value ?? null;
      console.log('要設定的 district 值:', districtValue);

      // 從 reportDistricts 陣列中找到對應的物件
      const matchedDistrict = districtValue
        ? this.reportDistricts.find(d => d.areaName === districtValue.areaName && d.zipCode === districtValue.zipCode)
        : null;
      console.log('從 reportDistricts 找到的物件:', matchedDistrict);

      // 使用 patchValue 並設定 emitEvent: false 避免觸發 valueChanges
      this.form.patchValue({
        reportCity: cityValue,
        reportDistrict: matchedDistrict ?? null,
        reportAddress: this.form.get('address')?.value ?? '',
      }, { emitEvent: false });

      console.log('設定後的 reportDistrict:', this.form.get('reportDistrict')?.value);

      //收件人資料強制勾"本人"，並且禁用
      this.form.get('deliveryRecipient')?.setValue(API_PARAMS.DELIVERY_METHOD.SELF.code ?? '01');
      this.form.get('deliveryRecipient')?.disable();

      // 填入會員本人資料
      const createMemberRequest = this.sharedStorageService.get('createMemberRequest');
      if (createMemberRequest) {
        this.form.patchValue({
          recipient: createMemberRequest.name ?? '',
          recipientCell: createMemberRequest.cell ?? '',
        });
      }
    } else {
      // 取消勾選 → 清空報告地址欄位
      console.log('清空 reportCity, reportDistrict, reportAddress');

      // 清空 reportDistricts 陣列
      this.reportDistricts = [];

      // 使用 patchValue 並設定 emitEvent: false 避免觸發 valueChanges
      this.form.patchValue({
        reportCity: '',
        reportDistrict: null,
        reportAddress: '',
        recipient: this.originalRecipient ?? '',
        recipientCell: this.originalRecipientCell ?? '',
      }, { emitEvent: false });


      //還原收件人資料變可選
      this.form.get('deliveryRecipient')?.enable();
    }
    this.selectedReportDistrict =
      this.form.get('reportDistrict')?.value ?? null;
    console.log('最終 selectedReportDistrict:', this.selectedReportDistrict);
  }

  // 取得國內檢查報告地址錯誤訊息
  getReportAddressErrorMessage(): string {
    if (!this.showFormError) return '';
    const errors: string[] = [];
    if (this.form.get('reportCity')?.errors)
      errors.push(this.translateService.instant('appointment-info.city'));
    if (this.form.get('reportDistrict')?.errors)
      errors.push(this.translateService.instant('appointment-info.district'));
    if (this.form.get('reportAddress')?.errors)
      errors.push(
        this.translateService.instant('appointment-info.detailed-address')
      );
    return errors.length
      ? this.translateService.instant('error.please-fill-in', {
          fieldName: errors.join('、'),
        })
      : '';
  }

  // 表單提交
  onSubmit() {
    this.showFormError = true;
    if (this.form.valid) {
      const formValue = this.form.value;

      // 改寫 district、reportDistrict(原{})，只傳出去 areaName、zipCode
      const submitValue = {
        ...formValue,
        zipCode: formValue.district?.zipCode ?? '',
        reportZipCode: formValue.reportDistrict?.zipCode ?? '',
        district: formValue.district?.areaName ?? null,
        reportDistrict: formValue.reportDistrict?.areaName ?? null,
        languageNoDesc: this.languages.find(
          l => l.uno === formValue.language
        )?.noDesc ?? '',
        language: formValue.language,
        deliveryMethod:formValue.deliveryMethod,
        deliveryRecipient:formValue.deliveryRecipient,
        recipient:formValue.recipient,
        recipientCell:formValue.recipientCell,
        foreignAddress: formValue.foreignAddress,
      };
      console.log('submitValue:', submitValue);
      this.dialogRef.close(submitValue as ModifyExamReportDialogModel,);
    }
  }

  // 關閉dialog
  closeDialog() {
    this.dialogRef.close(null);
  }

  protected readonly cities = CITIES.map(
    (city: { cityName: string }) => city.cityName
  ); //縣市下拉選單
  protected districts: {
    areaName: string;
    zipCode: string;
    areaEngName: string;
  }[] = []; //檢前資料地址-行政區下拉選單
  protected reportDistricts: {
    areaName: string;
    zipCode: string;
    areaEngName: string;
  }[] = []; // 檢查報告地址-行政區下拉選單
  selectedDistrict: {
    areaName: string;
    zipCode: string;
    areaEngName: string;
  } | null = null; // 檢前資料地址-已選擇的行政區
  selectedReportDistrict: {
    areaName: string;
    zipCode: string;
    areaEngName: string;
  } | null = null; // 檢查報告地址-已選擇的行政區
  protected readonly API_PARAMS = API_PARAMS;

  // 比較函數，用於 mat-select 比較物件
  compareDistricts(d1: District | null, d2: District | null): boolean {
    return d1?.areaName === d2?.areaName && d1?.zipCode === d2?.zipCode;
  }

  // 新增方法：檢查報告地址是否與檢前資料完全相同
  private checkAndUpdateIsChecked(): void {
    const reportCity = this.form.get('reportCity')?.value;
    const reportDistrict = this.form.get('reportDistrict')?.value;
    const reportAddress = this.form.get('reportAddress')?.value;

    const preExamCity = this.form.get('city')?.value;
    const preExamDistrict = this.form.get('district')?.value;
    const preExamAddress = this.form.get('address')?.value;

    const hasEmptyFields = !reportCity || !reportDistrict || !reportAddress || 
                         !preExamCity || !preExamDistrict || !preExamAddress;

    // 如果有空值,則不判斷為相同
    if (hasEmptyFields) {
      this.isChecked = false;
      this.updateBookingInfoIsChecked();
      return;
    }

    // 檢查三個欄位是否都相同
    const isSame = 
      reportCity === preExamCity &&
      reportDistrict?.areaName === preExamDistrict?.areaName &&
      reportDistrict?.zipCode === preExamDistrict?.zipCode &&
      reportAddress === preExamAddress;

    if (isSame) {
      this.isChecked = true;
    } else {
      this.isChecked = false;
    }

    this.updateBookingInfoIsChecked();
  }
}
